import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import GoogleMapReact from 'google-map-react'
import { map, size } from 'lodash-es'
import PropTypes from 'prop-types'
import useSupercluster from 'use-supercluster'

import { Map as MapContainer } from '../../../core/components/styled'
import { UK_COORDINATES } from '../../../core/constants'
import { getEnvironmentVariable } from '../../../core/helpers'

import { createPinDataFromLocations, fitMapToPins } from '../../utils'

import { LocationMapPin, MapClusterMarker } from './sections'

const DEFAULT_ZOOM = 6

const BrandDefinitionMap = ({
  center,
  onMapInitialized,
  locations,
  disabledLocations,
}) => {
  const mapRef = useRef(null)
  const [isInitialized, setIsInitialized] = useState(false)
  const [bounds, setBounds] = useState(null)
  const [zoom, setZoom] = useState(DEFAULT_ZOOM)

  const enabledPins = useMemo(
    () => createPinDataFromLocations(locations, false),
    [locations],
  )

  const disabledPins = useMemo(
    () => createPinDataFromLocations(disabledLocations, true),
    [disabledLocations],
  )

  useEffect(() => {
    if (isInitialized) {
      fitMapToPins({
        ...mapRef.current,
        pins: enabledPins,
        defaultCenter: center,
        defaultZoom: DEFAULT_ZOOM,
      })
    }
  }, [isInitialized, enabledPins, center])

  const { clusters, supercluster } = useSupercluster({
    points: disabledPins,
    bounds,
    zoom,
    options: { radius: 60, maxZoom: 20 },
  })

  const initialize = useCallback(
    ({ map, maps: googleMaps }) => {
      mapRef.current = { map, googleMaps }
      setIsInitialized(true)
      onMapInitialized({ map, googleMaps })
    },
    [onMapInitialized],
  )

  const generatePinKey = useCallback(
    ({ index, locationId }) => `${index}-${locationId}`,
    [],
  )

  return (
    <MapContainer sticky>
      <GoogleMapReact
        bootstrapURLKeys={{
          key: getEnvironmentVariable('REACT_APP_GOOGLE_API_KEY'),
        }}
        defaultCenter={center}
        defaultZoom={DEFAULT_ZOOM}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={initialize}
        onChange={({ zoom, bounds }) => {
          setZoom(zoom)
          setBounds([
            bounds.nw.lng,
            bounds.se.lat,
            bounds.se.lng,
            bounds.nw.lat,
          ])
        }}
      >
        {map(enabledPins, enabledPin => {
          const [longitude, latitude] = enabledPin.geometry.coordinates
          const {
            cluster: isCluster,
            point_count: pinsCount,
            ...pinProps
          } = enabledPin.properties

          return (
            <LocationMapPin
              key={generatePinKey(pinProps)}
              lat={latitude}
              lng={longitude}
              {...pinProps}
            />
          )
        })}

        {map(clusters, cluster => {
          const [longitude, latitude] = cluster.geometry.coordinates
          const {
            cluster: isCluster,
            point_count: pinsCount,
            ...pinProps
          } = cluster.properties

          if (!isCluster) {
            return (
              <LocationMapPin
                key={generatePinKey(pinProps)}
                lat={latitude}
                lng={longitude}
                disabled
                {...pinProps}
              />
            )
          }

          return (
            <MapClusterMarker
              key={`cluster-${cluster.id}`}
              lat={latitude}
              lng={longitude}
              cluster={cluster}
              pinsCount={pinsCount}
              totalPinsCount={size(enabledPins)}
              onClick={() => {
                const expansionZoom = Math.min(
                  supercluster.getClusterExpansionZoom(cluster.id),
                  20,
                )
                mapRef.current.map.setZoom(expansionZoom)
                mapRef.current.map.panTo({ lat: latitude, lng: longitude })
              }}
            />
          )
        })}
      </GoogleMapReact>
    </MapContainer>
  )
}

BrandDefinitionMap.propTypes = {
  center: PropTypes.object,
  locations: PropTypes.array.isRequired,
  disabledLocations: PropTypes.array.isRequired,
  onMapInitialized: PropTypes.func.isRequired,
}

BrandDefinitionMap.defaultProps = {
  center: UK_COORDINATES,
}

export default BrandDefinitionMap
